import { useTranslation } from 'react-i18next'

import {
  Badge,
  Box,
  Flex,
  LightModeSemanticColors,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaArrowDownRegular, FaArrowUpRegular } from '@enechain/ecloud-icons'

import { FUEL_LABELS } from '~/constants/supplyCapacityChange'
import type { AreaBlock } from '~/pages/SupplyCapacityChange/models/supplyCapacity'
import {
  fmtMMDD,
  fmtMw,
  mwOf,
  pressureOf,
} from '~/pages/SupplyCapacityChange/models/supplyCapacity'

const borderColor = LightModeSemanticColors.Border.lowEmphasis.value
const headerBg = LightModeSemanticColors.Surface.secondary.value
const zebraBg = LightModeSemanticColors.Surface.secondary.value
// 表の見出し行の地 (UI 辞書 tokens.md の Surface.tableHeader)
const tableHeaderBg = LightModeSemanticColors.Surface.tableHeader.value
const accentText = LightModeSemanticColors.Text.accentPrimary.value
const tealColor = LightModeSemanticColors.Brand.teal.value
const negColor = LightModeSemanticColors.Text.negativeHigh.value
const posColor = LightModeSemanticColors.Text.positiveMid.value
const periodColor = LightModeSemanticColors.Text.midEmphasis.value

const thStyle: React.CSSProperties = {
  border: `1px solid ${borderColor}`,
  padding: '4px 8px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  background: tableHeaderBg,
  fontWeight: 700,
}
const tdStyle: React.CSSProperties = {
  border: `1px solid ${borderColor}`,
  padding: '4px 8px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

// 全エリアブロックで共通の列幅 (= table-layout:fixed と組で、 ブロック間の
// 列区切り縦線を同一 x に揃える [mock差異]。 design は auto でブロック毎に食い違う)
const COL_WIDTHS = [
  '3rem', // 日
  '3.5rem', // 区分
  '14%', // 事業者 (可変)
  '20%', // 発電所 (可変)
  '5.5rem', // 形式
  '4.5rem', // ユニット
  '3.5rem', // MW
  '6.5rem', // 期間 (MM/DD〜MM/DD が見切れない固定幅)
]

type Props = {
  blocks: AreaBlock[]
}

export const AreaTables = ({ blocks }: Props): React.ReactElement => {
  const { t } = useTranslation()

  if (blocks.length === 0) {
    return (
      <Box
        padding={4}
        color="Text.midEmphasis"
        fontSize="sm"
        textAlign="center"
      >
        {t('page:supplyCapacityChange.area.empty')}
      </Box>
    )
  }

  return (
    <Box
      gap={3}
      gridTemplateColumns="repeat(auto-fill, minmax(560px, 1fr))"
      display="grid"
    >
      {blocks.map((block) => {
        // 価格圧力の符号判定は model の pressureOf に集約
        const { isFalling, tone } = pressureOf(block.netMw)
        const netCls = tone === 'positive' ? posColor : negColor
        return (
          <Box
            key={block.area}
            overflow="hidden"
            border={`1px solid ${borderColor}`}
            borderRadius="lg"
            backgroundColor="Surface.primary"
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
              padding="6px 12px"
              borderBottom={`1px solid ${borderColor}`}
              backgroundColor={headerBg}
            >
              <Text as="span" textStyle="subSectionTitle">
                {block.area}
              </Text>
              {/* eslint-disable-next-line ebalance/no-adhoc-typography -- net MW 値 md/bold (数値強調、design :77 .scc-area-net = md/700 一致。数値への heading role 付与は役割乖離で defer) */}
              <Text
                as="span"
                alignItems="center"
                gap={1}
                display="inline-flex"
                fontSize="md"
                fontWeight="bold"
                style={{ color: netCls }}
              >
                {`${fmtMw(block.netMw)} MW`}
                {isFalling ? (
                  <FaArrowDownRegular boxSize="11px" />
                ) : (
                  <FaArrowUpRegular boxSize="11px" />
                )}
              </Text>
            </Flex>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 12,
                tableLayout: 'fixed',
              }}
            >
              <colgroup>
                {COL_WIDTHS.map((w, i) => (
                  <col key={`col-${i}`} style={w ? { width: w } : undefined} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: 'center' }}>
                    {t('page:supplyCapacityChange.area.colDay')}
                  </th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>
                    {t('page:supplyCapacityChange.area.colType')}
                  </th>
                  <th style={thStyle}>
                    {t('page:supplyCapacityChange.area.colCompany')}
                  </th>
                  <th style={thStyle}>
                    {t('page:supplyCapacityChange.area.colPlant')}
                  </th>
                  <th style={thStyle}>
                    {t('page:supplyCapacityChange.area.colFuel')}
                  </th>
                  <th style={thStyle}>
                    {t('page:supplyCapacityChange.area.colUnit')}
                  </th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>
                    {t('page:supplyCapacityChange.area.colMw')}
                  </th>
                  <th style={thStyle}>
                    {t('page:supplyCapacityChange.area.colPeriod')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, idx) => {
                  const isStart = row.kind === 'start'
                  const dayColor =
                    row.dayKind === 'today' ? accentText : tealColor
                  const dayLabel =
                    row.dayKind === 'today'
                      ? t('page:supplyCapacityChange.area.dayToday')
                      : t('page:supplyCapacityChange.area.dayTomorrow')
                  const rowBg = idx % 2 === 1 ? zebraBg : undefined
                  return (
                    <tr
                      key={`${row.dayKind}-${row.kind}-${row.item.plant}-${row.item.unit}`}
                      style={{ background: rowBg }}
                    >
                      <td
                        style={{
                          ...tdStyle,
                          width: '3rem',
                          textAlign: 'center',
                          fontWeight: 700,
                          color: dayColor,
                        }}
                      >
                        {dayLabel}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          width: '3.2rem',
                          textAlign: 'center',
                        }}
                      >
                        {/* 停止開始/終了は状態表示なので DS Badge (2026-08-31
                            デザイナー指摘「Badge UI は DS 準拠」)。 開始 = 供給減 =
                            negative / 終了 = 供給回復 = positive。 */}
                        <Badge status={isStart ? 'negative' : 'positive'}>
                          {isStart
                            ? t('page:supplyCapacityChange.area.tagStart')
                            : t('page:supplyCapacityChange.area.tagEnd')}
                        </Badge>
                      </td>
                      <td style={tdStyle}>{row.item.company}</td>
                      <td style={tdStyle}>{row.item.plant}</td>
                      {/* [DS差異] 燃料種別は plain text。 design は燃料ごとの色付き badge を
                          定義するが、 2026-08-31 デザイナー指摘「Badge UI 不要なら plain text
                          に」により変更 (色はチャート非連動の飾りで、 DS Badge の 5 status
                          でも 8 燃料を表現できない)。 prototype は後追いで更新する。 */}
                      <td style={tdStyle}>{FUEL_LABELS[row.item.fuel]}</td>
                      <td style={tdStyle}>{row.item.unit}</td>
                      <td
                        style={{
                          ...tdStyle,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                          color: isStart ? negColor : posColor,
                        }}
                      >
                        {Math.round(mwOf(row.item)).toLocaleString()}
                      </td>
                      <td style={{ ...tdStyle, color: periodColor }}>
                        {`${fmtMMDD(row.item.start)}〜${fmtMMDD(row.item.end)}`}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Box>
        )
      })}
    </Box>
  )
}
